import { gql } from '@apollo/client';
import { FieldRendererFragment, FormFragment } from '../generated/graphql';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form as FormikForm, Formik } from 'formik';
import SmallContentWrapper from './SmallContentWrapper';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Toast from '@oberoninternal/travelbase-ds/components/feedback/Toast';
import { Box } from '@rebass/grid';
import HtmlMapper from 'react-html-map';
import tagMap from '../constants/tagMap';
import FieldRenderer from './FieldRenderer';
import { buildInitialValues, CsrfResponse, subscribeByForm, toFormData } from '../utils/formUtils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const fragment = gql`
    fragment Form on FormInterface {
        id
        handle
        siteId

        settings {
            submitActionFormHide
            submitActionMessageHtml
            submitAction
            submitActionTab
            submitActionUrl
            integrations {
                handle
                enabled
            }
        }
        fields {
            ...FieldRenderer
            ...GroupField
        }
    }
`;

interface Props {
    form: FormFragment;
}

export const globalValidationMessages = defineMessages({
    required: {
        defaultMessage: 'Dit veld is verplicht',
    },
});

const SUBMIT_URL = `/api/cms/formie/submissions/submit/`;
const CSRF_URL = `/api/cms/formie/csrf/`;

const jsonOrError = <T extends unknown>(promise: Promise<Response>): Promise<T> =>
    promise.then(response =>
        !response.ok
            ? Promise.reject(new Error(`Server Error ${response.status} ${response.statusText}`))
            : response.json()
    );

const Form: FC<React.PropsWithChildren<Props>> = ({ form }) => {
    const initialValues = useMemo(() => buildInitialValues(form.fields), [form]);
    const [csrf, setCsrf] = useState<CsrfResponse | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const { locale } = useIntl();
    const myRef = useRef<null | HTMLFormElement>(null);
    const executeScroll = () => myRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    useEffect(() => {
        jsonOrError<CsrfResponse>(fetch(CSRF_URL, { credentials: 'include' }))
            .then(response => setCsrf(response))
            .catch(err => setError(err));
    }, []);

    const shouldHide = submitted && form.settings?.submitActionFormHide;
    const { executeRecaptcha } = useGoogleReCaptcha();
    const hasRecaptchaEnabled = form.settings?.integrations?.some(
        integration => integration?.handle === 'recaptcha' && integration?.enabled === true
    );

    const token = useRef<string>('');
    const handleReCaptchaVerify = useCallback(async () => {
        if (hasRecaptchaEnabled) {
            if (!executeRecaptcha) {
                // eslint-disable-next-line no-console
                console.log('Execute recaptcha not yet available');
            }

            if (executeRecaptcha) {
                token.current = await executeRecaptcha();
            }
        }
    }, [executeRecaptcha, hasRecaptchaEnabled]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    return (
        <SmallContentWrapper>
            <Formik
                onSubmit={async values => {
                    try {
                        const formData = toFormData(
                            values,
                            form.fields,
                            token.current,
                            form.handle,
                            form.siteId,
                            csrf as CsrfResponse
                        );

                        subscribeByForm(values, form.fields, locale);

                        await fetch(SUBMIT_URL, {
                            method: 'POST',
                            body: formData,
                            credentials: 'include',
                            redirect: 'manual',
                        }).then(response =>
                            // if the backend attempted to redirect us, assume all is well.
                            response.type === 'opaqueredirect'
                                ? Promise.resolve()
                                : Promise.reject(new Error(`Server Error ${response.status} ${response.statusText}`))
                        );

                        switch (form.settings?.submitAction) {
                            case 'url':
                                if (form.settings.submitActionTab === 'same-tab') {
                                    window.location.assign(form.settings?.submitActionUrl ?? '/');
                                } else {
                                    window.open(form.settings?.submitActionUrl ?? '/', '_blank');
                                }
                                break;
                            case 'reload':
                                window.location.reload();
                                break;
                            default:
                            case 'message':
                                setSubmitted(true);
                                executeScroll();
                                break;
                        }
                    } catch (ex) {
                        setError(ex as Error);
                    }
                }}
                initialValues={initialValues}
            >
                {({ isSubmitting }) => (
                    <FormikForm id="formElement" ref={myRef}>
                        {error && <ErrorMessage error={error} />}

                        {submitted && form.settings?.submitAction === 'message' && (
                            <HtmlMapper html={form.settings.submitActionMessageHtml ?? ''}>{tagMap}</HtmlMapper>
                        )}
                        {!shouldHide && (
                            <>
                                <FieldRenderer fields={(form.fields as FieldRendererFragment[]) ?? []} />
                                <Button type="submit" disabled={isSubmitting && !!csrf}>
                                    <FormattedMessage defaultMessage="Verzenden" />
                                </Button>
                            </>
                        )}
                    </FormikForm>
                )}
            </Formik>
        </SmallContentWrapper>
    );
};

export default Form;

const ErrorMessage = ({ error }: { error: Error }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.scrollIntoView({ block: 'center' });
    }, []);

    return (
        <Box mb={5} ref={ref}>
            <Toast title={<FormattedMessage defaultMessage="Formulier kan niet worden verzonden" />} variant="error">
                {error.message}
            </Toast>
        </Box>
    );
};
